export type ProductStatus = 'in_stock' | 'on_order';

export interface FaqItem {
  q: string;
  a: string;
}

export interface ApplicationItem {
  title: string;
  text: string;
}

export interface Feature {
  title: string;
}

export interface Product {
  slug: string;
  brand: string;
  model: string;
  fullName: string;

  status: ProductStatus;
  statusLabel: string;
  statusSecondary?: string;
  leadTime: string;
  location?: string;

  price: string;
  priceValue: number;
  pricePrefix?: 'от';

  availableForTesting: boolean;
  installmentAvailable: boolean;
  warranty?: string;
  condition?: 'refurbished' | 'supplier';
  conditionLabel?: string;
  freeDeliveryNote?: string;

  introText: string;
  shortDescription: string;
  overview: string[];
  features: Feature[];

  applications: ApplicationItem[];
  compatibilityNotice: string;
  requiredForMatch: string[];

  specs: [string, string][];
  termsList: [string, string][];
  faq: FaqItem[];

  image: string;
  imageLarge: string;
  imageAlt: string;

  metaTitle: string;
  metaDescription: string;
  h1: string;
  primaryCta: string;
}

const photo = (slug: string) => `/products/${slug}.webp`;
const photoLarge = (slug: string) => `/products/${slug}-large.webp`;

const COMMON_FAQ_ON_ORDER = (label: string): FaqItem[] => [
  {
    q: `Можно ли купить ${label} в наличии?`,
    a: 'Текущий статус двигателя — «Под заказ». Поставка осуществляется со склада поставщика, типовой срок 14–20 дней с момента подтверждения заявки. По актуальному наличию у поставщиков я уточню при обращении: ассортимент меняется.',
  },
  {
    q: 'Как проверяется совместимость с моей техникой?',
    a: 'Совместимость подтверждаем по VIN, серийному номеру двигателя или фотографии шильдика. По этим данным сверяем модификацию и комплектацию до выставления счёта.',
  },
  {
    q: 'Какой срок поставки в регион?',
    a: 'Типовой срок поставки — 14–20 дней с момента подтверждения заказа. После прибытия в Екатеринбург отправляем транспортной компанией в регион клиента.',
  },
  {
    q: 'Возможна ли оплата с НДС?',
    a: 'Да. Работаем с юридическими и физическими лицами, оплата возможна как с НДС, так и без НДС. Полный комплект документов предоставляется по запросу.',
  },
  {
    q: 'Как заключается договор?',
    a: 'После согласования комплектации и сроков подписывается договор поставки, в нём фиксируются модификация, цена, срок и условия гарантии.',
  },
];

export const products: Product[] = [
  // ====================================================================
  // 1. Caterpillar 3412E — в наличии в Екатеринбурге
  // ====================================================================
  {
    slug: 'caterpillar-3412e',
    brand: 'Caterpillar',
    model: '3412E',
    fullName: 'Caterpillar 3412E',

    status: 'in_stock',
    statusLabel: 'В наличии',
    leadTime: '1–2 рабочих дня после оплаты',
    location: 'Екатеринбург',

    price: '12 500 000 ₽',
    priceValue: 12500000,

    availableForTesting: true,
    installmentAvailable: true,
    warranty: '12 месяцев или 2000 моточасов',
    condition: 'refurbished',
    conditionLabel: 'Восстановлен на оригинальных запчастях Caterpillar',
    freeDeliveryNote:
      'Бесплатно до транспортной компании в Екатеринбурге или Свердловской области',

    introText:
      'Восстановлен на оригинальных запасных частях Caterpillar и готов к эксплуатации. Можно приехать, лично осмотреть двигатель и провести тестирование перед покупкой.',
    shortDescription:
      'Дизельный двигатель в сборе для специальной, промышленной и карьерной техники.',
    overview: [
      'Caterpillar 3412E — двенадцатицилиндровый дизельный двигатель серии 3400 объёмом 27 литров. Применяется в карьерной, промышленной, морской и нефтегазовой технике.',
      'Двигатель прошёл комплексное восстановление с использованием оригинальных запасных частей Caterpillar и подготовлен к дальнейшей эксплуатации. Перед продажей выполняется проверка работоспособности узлов и готовности двигателя к запуску.',
    ],
    features: [
      { title: 'В наличии в Екатеринбурге' },
      { title: 'Осмотр и тестирование перед покупкой' },
      { title: 'Гарантия 12 месяцев или 2000 моточасов' },
      { title: 'Отгрузка за 1–2 рабочих дня' },
    ],

    applications: [
      {
        title: 'Карьерная и горнодобывающая техника',
        text: 'Карьерные самосвалы, бульдозеры большого класса, фронтальные погрузчики и дробильные установки.',
      },
      {
        title: 'Строительная техника',
        text: 'Гусеничные экскаваторы тяжёлого класса, буровые и сваебойные установки.',
      },
      {
        title: 'Дизельные генераторы и электростанции',
        text: 'Промышленные, резервные и автономные дизель-генераторные установки большой мощности.',
      },
      {
        title: 'Нефтегазовое оборудование',
        text: 'Компрессорные станции, буровые установки и силовые модули нефтегазовых платформ.',
      },
      {
        title: 'Морская техника',
        text: 'Главные и вспомогательные судовые силовые установки, насосные станции.',
      },
      {
        title: 'Промышленное оборудование',
        text: 'Стационарные насосные станции, технологические установки большой мощности.',
      },
    ],
    compatibilityNotice:
      'Точная совместимость определяется по серийному номеру двигателя, модели техники и требуемой комплектации.',
    requiredForMatch: [
      'модель техники и её серийный номер',
      'серийный номер действующего двигателя',
      'фотография шильдика двигателя',
    ],

    specs: [
      ['Модель', 'Caterpillar 3412E'],
      ['Мощность', '476–860 кВт'],
      ['Рабочий объём', '27 л'],
      ['Количество цилиндров', '12'],
      ['Система охлаждения', 'Жидкостная'],
      ['Система впрыска', 'Электронная'],
      ['Экологический стандарт', 'EPA Tier 2'],
      ['Комплектность', 'По запросу'],
      ['Гарантия', '12 месяцев или 2000 моточасов'],
    ],
    termsList: [
      ['Цена', '12 500 000 ₽'],
      ['Статус', 'В наличии'],
      ['Местонахождение', 'Екатеринбург'],
      ['Отгрузка', '1–2 рабочих дня после оплаты'],
      ['Гарантия', '12 месяцев или 2000 моточасов'],
      [
        'Доставка',
        'Бесплатно до транспортной компании в Екатеринбурге или Свердловской области',
      ],
      ['Оплата', 'С НДС или без НДС'],
      ['Рассрочка', 'Обсуждается индивидуально'],
    ],
    faq: [
      {
        q: 'Можно ли проверить двигатель перед покупкой?',
        a: 'Да. Caterpillar 3412E находится в Екатеринбурге, доступен для осмотра и тестирования. Согласуем удобную дату — покажем двигатель, запуск и проверим комплектность вместе с вашим специалистом.',
      },
      {
        q: 'Где находится Caterpillar 3412E?',
        a: 'Двигатель находится в Екатеринбурге, по адресу ул. Высоцкого, 42/2, офис 401. Приём посетителей по записи, чтобы заранее подготовить документы и проводить запуск.',
      },
      {
        q: 'Можно ли приехать со своим механиком?',
        a: 'Да, можно приехать со своим механиком или техническим специалистом. Мы открыты к независимой проверке: осмотру, проверке узлов, сверке серийных номеров и оценке стабильности работы.',
      },
      {
        q: 'Какая гарантия предоставляется?',
        a: 'На двигатель предоставляется гарантия 12 месяцев или 2000 моточасов — что наступит раньше. Условия гарантии фиксируются в договоре до оплаты.',
      },
      {
        q: 'Возможна ли покупка в рассрочку?',
        a: 'Да, рассмотрим индивидуальный график оплаты для юридических лиц и предпринимателей. Условия зависят от первоначального взноса, срока и формы оплаты. Заявку рассматриваем после получения данных по компании-покупателю.',
      },
      {
        q: 'Что входит в комплектность двигателя?',
        a: 'Базовая комплектность согласовывается под задачу клиента. Точный перечень навесного оборудования, систем и интерфейсов уточняется до подписания договора, чтобы двигатель подошёл под вашу установку.',
      },
      {
        q: 'Как проверить совместимость с моей техникой?',
        a: 'Совместимость определяется по серийному номеру двигателя, модели техники и требуемой комплектации. Пришлите эти данные — я сверю модификацию и подтвержу установку.',
      },
      {
        q: 'Как организована доставка в другой регион?',
        a: 'Доставка до транспортной компании в Екатеринбурге или по Свердловской области — бесплатно. Дальше отправляем выбранной транспортной компанией в любой регион России, страхование и упаковку организуем по согласованию.',
      },
      {
        q: 'Какие документы предоставляются?',
        a: 'Договор поставки, счёт, акт приёма-передачи, гарантийные обязательства. По запросу предоставляем технический паспорт и серийные номера агрегатов.',
      },
      {
        q: 'Можно ли оплатить с НДС?',
        a: 'Да. Работаем как с НДС, так и без НДС — для юридических и физических лиц, отдельных предпринимателей.',
      },
    ],

    image: photo('caterpillar-3412e'),
    imageLarge: photoLarge('caterpillar-3412e'),
    imageAlt:
      'Восстановленный двигатель Caterpillar 3412E в наличии в Екатеринбурге',

    metaTitle:
      'Купить двигатель Caterpillar 3412E в Екатеринбурге — в наличии, гарантия',
    metaDescription:
      'Восстановленный двигатель Caterpillar 3412E в наличии в Екатеринбурге. Осмотр и тестирование перед покупкой, гарантия 12 месяцев или 2000 моточасов. Возможна рассрочка.',
    h1: 'Двигатель Caterpillar 3412E в наличии в Екатеринбурге',
    primaryCta: 'Записаться на тестирование',
  },

  // ====================================================================
  // 2. Caterpillar C15 — под заказ
  // ====================================================================
  {
    slug: 'caterpillar-c15',
    brand: 'Caterpillar',
    model: 'C15',
    fullName: 'Caterpillar C15',

    status: 'on_order',
    statusLabel: 'Под заказ',
    statusSecondary: 'Доступен у поставщика',
    leadTime: '14–20 дней',

    price: '6 900 000 ₽',
    priceValue: 6900000,

    availableForTesting: false,
    installmentAvailable: false,

    introText:
      'Шестицилиндровый дизельный двигатель Caterpillar C15 для тяжёлой грузовой и специальной техники. Подбираем модификацию под вашу технику и поставляем у проверенных поставщиков.',
    shortDescription: 'Двигатель для грузовой и специальной техники.',
    overview: [
      'Caterpillar C15 — рядный шестицилиндровый дизельный двигатель серии C, применяется в магистральных тягачах, карьерных самосвалах среднего класса и стационарных установках.',
      'Поставка осуществляется со склада поставщика после подтверждения модификации, комплектации и совместимости с вашей техникой. Конкретные сроки и условия фиксируются в договоре.',
    ],
    features: [
      { title: 'Подбор по серийному номеру' },
      { title: 'Поставка 14–20 дней' },
      { title: 'Сверка модификации до счёта' },
      { title: 'Доставка по России' },
    ],

    applications: [
      {
        title: 'Магистральные грузовые автомобили',
        text: 'Седельные тягачи и магистральные грузовики тяжёлого класса.',
      },
      {
        title: 'Карьерные самосвалы',
        text: 'Карьерные самосвалы среднего класса, шарнирно-сочленённые модели.',
      },
      {
        title: 'Строительная техника',
        text: 'Гусеничные экскаваторы и фронтальные погрузчики тяжёлого класса.',
      },
      {
        title: 'Стационарные генераторные установки',
        text: 'Промышленные и резервные дизель-генераторы средней мощности.',
      },
    ],
    compatibilityNotice:
      'Перед оформлением заявки проверьте совместимость по серийному номеру двигателя и модели техники. Caterpillar C15 выпускался в нескольких модификациях с разной комплектацией.',
    requiredForMatch: [
      'модель и год выпуска техники',
      'серийный номер действующего двигателя',
      'фотография шильдика двигателя',
      'требуемая комплектация',
    ],

    specs: [
      ['Модель', 'Caterpillar C15'],
      ['Тип', 'Рядный 6-цилиндровый дизельный двигатель'],
      ['Применение', 'Грузовая и специальная техника'],
      ['Состояние', 'Уточняется у поставщика'],
      ['Комплектность', 'По запросу'],
      ['Срок поставки', '14–20 дней'],
    ],
    termsList: [
      ['Цена', '6 900 000 ₽'],
      ['Статус', 'Под заказ'],
      ['Срок поставки', '14–20 дней'],
      ['Подбор', 'По серийному номеру и модели техники'],
      ['Оплата', 'С НДС или без НДС'],
      ['Доставка', 'Транспортной компанией в регион клиента'],
      ['Гарантия', 'Условия фиксируются в договоре поставки'],
    ],
    faq: [
      {
        q: 'Можно ли купить Caterpillar C15 в наличии?',
        a: 'Текущий статус — «Под заказ». Caterpillar C15 доступен у проверенных поставщиков, типовой срок поставки 14–20 дней с момента подтверждения заявки и оплаты.',
      },
      {
        q: 'Как уточнить модификацию C15?',
        a: 'Двигатель Caterpillar C15 выпускался в нескольких модификациях — для дальнобойных грузовиков, для самосвалов и для стационарных установок. Уточняем модификацию по серийному номеру двигателя и по модели техники.',
      },
      {
        q: 'Какие данные нужны для подбора?',
        a: 'Модель и год выпуска техники, серийный номер двигателя, фотография шильдика и требуемая комплектация. По этим данным сверяем модификацию до выставления счёта.',
      },
      {
        q: 'Какой срок поставки?',
        a: 'Типовой срок 14–20 дней с момента подтверждения заказа. После прибытия в Екатеринбург отправляем транспортной компанией в регион клиента.',
      },
      {
        q: 'Возможна ли оплата с НДС?',
        a: 'Да. Принимаем оплату с НДС и без НДС, работаем с юридическими и физическими лицами.',
      },
      {
        q: 'Как организована доставка?',
        a: 'Доставку в регион клиента организуем транспортными компаниями. Условия страхования и упаковки согласовываются отдельно.',
      },
    ],

    image: photo('caterpillar-c15'),
    imageLarge: photoLarge('caterpillar-c15'),
    imageAlt: 'Двигатель Caterpillar C15 для грузовой и специальной техники',

    metaTitle:
      'Купить двигатель Caterpillar C15 под заказ — цена и доставка по России',
    metaDescription:
      'Двигатель Caterpillar C15 для грузовой и специальной техники. Подбор комплектации, проверка совместимости и поставка по России за 14–20 дней.',
    h1: 'Двигатель Caterpillar C15 под заказ',
    primaryCta: 'Запросить поставку Caterpillar C15',
  },

  // ====================================================================
  // 3. Cummins QSK23-C760
  // ====================================================================
  {
    slug: 'cummins-qsk23-c760',
    brand: 'Cummins',
    model: 'QSK23-C760',
    fullName: 'Cummins QSK23-C760',

    status: 'on_order',
    statusLabel: 'Под заказ',
    statusSecondary: 'Доступен у поставщика',
    leadTime: '14–20 дней',

    price: '21 936 000 ₽',
    priceValue: 21936000,

    availableForTesting: false,
    installmentAvailable: false,

    introText:
      'Дизельный двигатель Cummins QSK23-C760 высокой мощности для карьерной, промышленной и нефтегазовой техники. Уточняем модификацию по серийному номеру и организуем поставку у поставщика.',
    shortDescription:
      'Дизельный двигатель большой мощности для карьерной и промышленной техники.',
    overview: [
      'Cummins QSK23-C760 — V-образный 12-цилиндровый дизельный двигатель семейства QSK23 объёмом 23 литра. Применяется в карьерной и горнодобывающей технике, на буровых установках и в составе крупных силовых модулей.',
      'Поставляется со склада поставщика после подтверждения модификации, комплектации и совместимости с задачей клиента.',
    ],
    features: [
      { title: 'Подбор под задачу' },
      { title: 'Поставка 14–20 дней' },
      { title: 'Сверка модификации до счёта' },
      { title: 'Доставка по России' },
    ],

    applications: [
      {
        title: 'Карьерная и горнодобывающая техника',
        text: 'Карьерные самосвалы большого класса, тяжёлые бульдозеры и дробильные комплексы.',
      },
      {
        title: 'Буровые и нефтегазовые установки',
        text: 'Силовые модули буровых установок, компрессорные и насосные станции.',
      },
      {
        title: 'Большие дизельные генераторы',
        text: 'Промышленные и автономные дизель-генераторные установки большой мощности.',
      },
      {
        title: 'Морская и портовая техника',
        text: 'Главные и вспомогательные судовые силовые установки.',
      },
    ],
    compatibilityNotice:
      'Перед оформлением заявки требуется сверка модификации по серийному номеру двигателя. Семейство QSK23 выпускалось в разных конфигурациях мощности и навески.',
    requiredForMatch: [
      'модель техники и её серийный номер',
      'требуемая мощность и комплектация',
      'серийный номер действующего двигателя',
      'фотография шильдика двигателя',
    ],

    specs: [
      ['Модель', 'Cummins QSK23-C760'],
      ['Тип', 'V-образный 12-цилиндровый дизельный двигатель'],
      ['Применение', 'Карьерная, промышленная и специальная техника'],
      ['Состояние', 'Уточняется у поставщика'],
      ['Комплектность', 'По запросу'],
      ['Срок поставки', '14–20 дней'],
    ],
    termsList: [
      ['Цена', '21 936 000 ₽'],
      ['Статус', 'Под заказ'],
      ['Срок поставки', '14–20 дней'],
      ['Подбор', 'По серийному номеру и задаче'],
      ['Оплата', 'С НДС или без НДС'],
      ['Доставка', 'Транспортной компанией в регион клиента'],
      ['Гарантия', 'Условия фиксируются в договоре поставки'],
    ],
    faq: [
      {
        q: 'Можно ли купить Cummins QSK23-C760 в наличии?',
        a: 'Текущий статус — «Под заказ». Cummins QSK23-C760 доступен у проверенных поставщиков, типовой срок поставки 14–20 дней с момента подтверждения заказа.',
      },
      {
        q: 'Как проверить совместимость с моей техникой?',
        a: 'Семейство QSK23 выпускалось в нескольких модификациях. Подтверждаем совместимость по серийному номеру двигателя, модели техники и требуемой мощности.',
      },
      ...COMMON_FAQ_ON_ORDER('Cummins QSK23-C760').slice(2),
    ],

    image: photo('cummins-qsk23-c760'),
    imageLarge: photoLarge('cummins-qsk23-c760'),
    imageAlt:
      'Двигатель Cummins QSK23-C760 для карьерной и промышленной техники',

    metaTitle:
      'Купить Cummins QSK23-C760 — двигатель под заказ с доставкой',
    metaDescription:
      'Двигатель Cummins QSK23-C760 для карьерной, промышленной и специальной техники. Подбор по серийному номеру, поставка по России за 14–20 дней.',
    h1: 'Двигатель Cummins QSK23-C760 под заказ',
    primaryCta: 'Получить предложение',
  },

  // ====================================================================
  // 4. Cummins ISLe 400 40/50
  // ====================================================================
  {
    slug: 'cummins-isle-400',
    brand: 'Cummins',
    model: 'ISLe 400 40/50',
    fullName: 'Cummins ISLe 400 40/50',

    status: 'on_order',
    statusLabel: 'Под заказ',
    statusSecondary: 'Доступен у поставщика',
    leadTime: '14–20 дней',

    price: '912 000 ₽',
    priceValue: 912000,

    availableForTesting: false,
    installmentAvailable: false,

    introText:
      'Шестицилиндровый дизельный двигатель Cummins ISLe 400 для грузовой, автобусной и специальной техники. Подбираем модификацию по VIN или фото шильдика и поставляем под заказ.',
    shortDescription: 'Двигатель для грузовой, автобусной и специальной техники.',
    overview: [
      'Cummins ISLe 400 — рядный 6-цилиндровый дизельный двигатель серии ISLe мощностью 400 л.с., соответствующий нормам Евро-4 и Евро-5. Применяется в магистральных грузовиках, междугородних автобусах и спецтехнике китайских и российских производителей.',
      'Поставляется со склада поставщика после подтверждения модификации и комплектации.',
    ],
    features: [
      { title: 'Подбор по VIN' },
      { title: 'Поставка 14–20 дней' },
      { title: 'Сверка модификации до счёта' },
      { title: 'Доставка по России' },
    ],

    applications: [
      {
        title: 'Магистральные грузовые автомобили',
        text: 'Тягачи и грузовики тяжёлого и среднего класса (Foton, Dongfeng, КАМАЗ-частично).',
      },
      {
        title: 'Междугородние автобусы',
        text: 'Туристические и междугородние автобусы класса B и C.',
      },
      {
        title: 'Специальная техника',
        text: 'Шасси и установки на специализированных машинах с двигателем ISLe.',
      },
      {
        title: 'Коммунальная техника',
        text: 'Подметально-уборочные машины и тяжёлая коммунальная техника.',
      },
    ],
    compatibilityNotice:
      'Модификации ISLe 400 отличаются по экологическому классу и навеске. Совместимость подтверждаем по VIN и серийному номеру.',
    requiredForMatch: [
      'VIN техники',
      'серийный номер действующего двигателя',
      'фотография шильдика двигателя',
      'экологический класс (Евро-4/Евро-5)',
    ],

    specs: [
      ['Модель', 'Cummins ISLe 400 40/50'],
      ['Тип', 'Рядный 6-цилиндровый дизельный двигатель'],
      ['Мощность', '400 л.с.'],
      ['Применение', 'Грузовая, автобусная и специальная техника'],
      ['Экологический класс', 'Уточняется по модификации'],
      ['Комплектность', 'По запросу'],
      ['Срок поставки', '14–20 дней'],
    ],
    termsList: [
      ['Цена', '912 000 ₽'],
      ['Статус', 'Под заказ'],
      ['Срок поставки', '14–20 дней'],
      ['Подбор', 'По VIN или серийному номеру'],
      ['Оплата', 'С НДС или без НДС'],
      ['Доставка', 'Транспортной компанией в регион клиента'],
      ['Гарантия', 'Условия фиксируются в договоре поставки'],
    ],
    faq: [
      {
        q: 'Можно ли купить Cummins ISLe 400 в наличии?',
        a: 'Текущий статус — «Под заказ». Cummins ISLe 400 40/50 доступен у поставщика, типовой срок поставки 14–20 дней после подтверждения заказа.',
      },
      {
        q: 'Чем отличается ISLe 400 40 от ISLe 400 50?',
        a: 'Это разные модификации одного семейства, отличающиеся экологическим классом и компоновкой навесного оборудования. Точную модификацию подбираем по VIN или серийному номеру вашей техники.',
      },
      {
        q: 'Подойдёт ли ISLe 400 для моего грузовика?',
        a: 'Подойдёт, если ваша техника изначально комплектовалась этим двигателем. Подтверждаем совместимость по VIN, серийному номеру двигателя и шильдику.',
      },
      ...COMMON_FAQ_ON_ORDER('Cummins ISLe 400 40/50').slice(2),
    ],

    image: photo('cummins-isle-400'),
    imageLarge: photoLarge('cummins-isle-400'),
    imageAlt:
      'Двигатель Cummins ISLe 400 40/50 для грузовой, автобусной и специальной техники',

    metaTitle:
      'Двигатель Cummins ISLe 400 40/50 — цена и поставка по России',
    metaDescription:
      'Двигатель Cummins ISLe 400 40/50 для грузовой, автобусной и специальной техники. Проверка совместимости и поставка под заказ за 14–20 дней.',
    h1: 'Двигатель Cummins ISLe 400 40/50 под заказ',
    primaryCta: 'Проверить возможность поставки',
  },

  // ====================================================================
  // 5. Mercedes-Benz OM501LA
  // ====================================================================
  {
    slug: 'mercedes-om501la',
    brand: 'Mercedes-Benz',
    model: 'OM501LA',
    fullName: 'Mercedes-Benz OM501LA',

    status: 'on_order',
    statusLabel: 'Под заказ',
    statusSecondary: 'Доступен у поставщика',
    leadTime: '14–20 дней',

    price: 'от 3 540 000 ₽',
    priceValue: 3540000,
    pricePrefix: 'от',

    availableForTesting: false,
    installmentAvailable: false,

    introText:
      'V-образный дизельный двигатель Mercedes-Benz OM501LA для тяжёлых грузовиков Actros и Axor. Подбираем модификацию по VIN и серийному номеру и поставляем под заказ.',
    shortDescription: 'Дизельный двигатель для грузовых автомобилей Mercedes-Benz.',
    overview: [
      'Mercedes-Benz OM501LA — V-образный 6-цилиндровый дизельный двигатель объёмом 12 литров. Устанавливался на магистральные тягачи Actros, Axor и тяжёлые грузовики Mercedes-Benz первого и второго поколений.',
      'Поставка под заказ. Перед выставлением счёта подтверждаем модификацию по VIN и серийному номеру.',
    ],
    features: [
      { title: 'Подбор по VIN Mercedes-Benz' },
      { title: 'Поставка 14–20 дней' },
      { title: 'Сверка модификации до счёта' },
      { title: 'Доставка по России' },
    ],

    applications: [
      {
        title: 'Магистральные тягачи Mercedes-Benz Actros',
        text: 'Первое и второе поколения Actros с двигателем OM501LA.',
      },
      {
        title: 'Грузовики Mercedes-Benz Axor',
        text: 'Дальнобойные и строительные грузовики Axor.',
      },
      {
        title: 'Тяжёлая строительная техника',
        text: 'Самосвалы и спецтехника на шасси Mercedes-Benz.',
      },
      {
        title: 'Седельные тягачи',
        text: 'Седельные тягачи для перевозки крупногабаритных грузов.',
      },
    ],
    compatibilityNotice:
      'OM501LA выпускался в нескольких модификациях с разной мощностью и экологическим классом. Совместимость подтверждаем по VIN автомобиля и серийному номеру двигателя.',
    requiredForMatch: [
      'VIN автомобиля Mercedes-Benz',
      'серийный номер действующего двигателя',
      'фотография шильдика двигателя',
      'требуемая мощность',
    ],

    specs: [
      ['Модель', 'Mercedes-Benz OM501LA'],
      ['Тип', 'V-образный 6-цилиндровый дизельный двигатель'],
      ['Рабочий объём', '12 л'],
      ['Применение', 'Грузовые автомобили Mercedes-Benz'],
      ['Состояние', 'Уточняется у поставщика'],
      ['Комплектность', 'По запросу'],
      ['Срок поставки', '14–20 дней'],
    ],
    termsList: [
      ['Цена', 'от 3 540 000 ₽'],
      ['Статус', 'Под заказ'],
      ['Срок поставки', '14–20 дней'],
      ['Подбор', 'По VIN и серийному номеру'],
      ['Оплата', 'С НДС или без НДС'],
      ['Доставка', 'Транспортной компанией в регион клиента'],
      ['Гарантия', 'Условия фиксируются в договоре поставки'],
    ],
    faq: [
      {
        q: 'Можно ли купить OM501LA в наличии?',
        a: 'Текущий статус — «Под заказ». Mercedes-Benz OM501LA доступен у проверенных поставщиков, типовой срок поставки 14–20 дней с момента подтверждения заказа.',
      },
      {
        q: 'Подойдёт ли OM501LA для моего Actros?',
        a: 'Двигатель OM501LA устанавливался на первое и второе поколения Actros и на грузовики Axor. Совместимость подтверждаем по VIN автомобиля и серийному номеру двигателя.',
      },
      {
        q: 'Какие модификации OM501LA бывают?',
        a: 'OM501LA выпускался с разной мощностью и в разных экологических классах. Точную модификацию подбираем по требованиям и совместимости с вашим автомобилем.',
      },
      ...COMMON_FAQ_ON_ORDER('Mercedes-Benz OM501LA').slice(2),
    ],

    image: photo('mercedes-om501la'),
    imageLarge: photoLarge('mercedes-om501la'),
    imageAlt:
      'Двигатель Mercedes-Benz OM501LA для грузовиков Actros и Axor',

    metaTitle: 'Купить двигатель Mercedes-Benz OM501LA под заказ',
    metaDescription:
      'Двигатель Mercedes-Benz OM501LA для грузовой техники. Подбор по VIN и номеру агрегата, поставка по России за 14–20 дней.',
    h1: 'Двигатель Mercedes-Benz OM501LA под заказ',
    primaryCta: 'Подобрать OM501LA',
  },

  // ====================================================================
  // 6. John Deere 4045
  // ====================================================================
  {
    slug: 'john-deere-4045',
    brand: 'John Deere',
    model: '4045',
    fullName: 'John Deere 4045',

    status: 'on_order',
    statusLabel: 'Под заказ',
    statusSecondary: 'Доступен у поставщика',
    leadTime: '14–20 дней',

    price: '2 016 000 ₽',
    priceValue: 2016000,

    availableForTesting: false,
    installmentAvailable: false,

    introText:
      'Четырёхцилиндровый дизельный двигатель John Deere 4045 (PowerTech) для сельскохозяйственной, строительной и специальной техники. Подбираем модификацию по серийному номеру и поставляем под заказ.',
    shortDescription:
      'Двигатель для сельскохозяйственной, строительной и специальной техники.',
    overview: [
      'John Deere 4045 — рядный 4-цилиндровый дизельный двигатель серии PowerTech объёмом 4.5 литра. Применяется в тракторах и комбайнах John Deere, в строительной и специальной технике различных производителей, а также в составе стационарных энергоустановок.',
      'Поставляется со склада поставщика после подтверждения модификации (PowerTech E / M / Plus / PSS / PVS) и комплектации.',
    ],
    features: [
      { title: 'Подбор по серийному номеру' },
      { title: 'Поставка 14–20 дней' },
      { title: 'Сверка модификации до счёта' },
      { title: 'Доставка по России' },
    ],

    applications: [
      {
        title: 'Сельскохозяйственная техника',
        text: 'Тракторы и комбайны John Deere, кормоуборочная техника.',
      },
      {
        title: 'Строительная техника',
        text: 'Мини-погрузчики, экскаваторы среднего класса и грейдеры.',
      },
      {
        title: 'Специальная техника',
        text: 'Промышленные погрузчики, лесозаготовительная техника.',
      },
      {
        title: 'Стационарные энергоустановки',
        text: 'Дизельные генераторы и насосные станции средней мощности.',
      },
    ],
    compatibilityNotice:
      'Двигатель 4045 выпускался в нескольких модификациях семейства PowerTech (E, M, Plus, PSS, PVS), отличающихся системой топливоподачи и экологическим классом. Подбор только по серийному номеру.',
    requiredForMatch: [
      'модель и год выпуска техники',
      'серийный номер действующего двигателя',
      'фотография шильдика двигателя',
      'модификация семейства PowerTech',
    ],

    specs: [
      ['Модель', 'John Deere 4045'],
      ['Тип', 'Рядный 4-цилиндровый дизельный двигатель'],
      ['Рабочий объём', '4.5 л'],
      ['Применение', 'Сельскохозяйственная, строительная и специальная техника'],
      ['Семейство', 'PowerTech'],
      ['Комплектность', 'По запросу'],
      ['Срок поставки', '14–20 дней'],
    ],
    termsList: [
      ['Цена', '2 016 000 ₽'],
      ['Статус', 'Под заказ'],
      ['Срок поставки', '14–20 дней'],
      ['Подбор', 'По серийному номеру (модификация PowerTech)'],
      ['Оплата', 'С НДС или без НДС'],
      ['Доставка', 'Транспортной компанией в регион клиента'],
      ['Гарантия', 'Условия фиксируются в договоре поставки'],
    ],
    faq: [
      {
        q: 'Можно ли купить John Deere 4045 в наличии?',
        a: 'Текущий статус — «Под заказ». Двигатель John Deere 4045 доступен у проверенных поставщиков, типовой срок поставки 14–20 дней.',
      },
      {
        q: 'Какие модификации John Deere 4045 существуют?',
        a: 'Двигатель 4045 выпускался в нескольких модификациях семейства PowerTech: E, M, Plus, PSS и PVS. Они отличаются системой топливоподачи и экологическим классом. Подбираем под вашу технику.',
      },
      {
        q: 'Какие данные нужны для подбора?',
        a: 'Модель и год выпуска техники, серийный номер действующего двигателя, фотография шильдика и нужная модификация PowerTech, если известна.',
      },
      ...COMMON_FAQ_ON_ORDER('John Deere 4045').slice(2),
    ],

    image: photo('john-deere-4045'),
    imageLarge: photoLarge('john-deere-4045'),
    imageAlt:
      'Двигатель John Deere 4045 для сельскохозяйственной и строительной техники',

    metaTitle: 'Купить двигатель John Deere 4045 — поставка по России',
    metaDescription:
      'Двигатель John Deere 4045 для сельскохозяйственной, строительной и специальной техники. Подбор модификации и доставка за 14–20 дней.',
    h1: 'Двигатель John Deere 4045 под заказ',
    primaryCta: 'Запросить двигатель John Deere 4045',
  },
];

export const findProduct = (slug: string) => products.find((p) => p.slug === slug);

export const relatedProducts = (slug: string, count = 3) =>
  products.filter((p) => p.slug !== slug).slice(0, count);

export const allBrands = () =>
  Array.from(new Set(products.map((p) => p.brand)));

export const brandSiblings = (slug: string) => {
  const me = findProduct(slug);
  if (!me) return [];
  return products.filter((p) => p.brand === me.brand && p.slug !== slug);
};
